import {
  SmsConsentBasis,
  SmsConsentEventAction,
  SmsConsentStatus,
} from "../../generated/graphql";

export const STATUS_LABELS: Record<SmsConsentStatus, string> = {
  [SmsConsentStatus.Granted]: "May be texted",
  [SmsConsentStatus.Pending]: "Awaiting confirmation",
  [SmsConsentStatus.Revoked]: "Opted out",
};

export const BASIS_LABELS: Record<SmsConsentBasis, string> = {
  [SmsConsentBasis.CarrierProvided]: "Carrier provided",
  [SmsConsentBasis.WebForm]: "Web form opt-in",
};

export const EVENT_LABELS: Record<SmsConsentEventAction, string> = {
  [SmsConsentEventAction.CarrierGranted]: "Added from a delivery order",
  [SmsConsentEventAction.Requested]: "Opt-in requested on the web form",
  [SmsConsentEventAction.Confirmed]: "Opt-in confirmed from the phone",
  [SmsConsentEventAction.Revoked]: "Opted out",
  [SmsConsentEventAction.Resumed]: "Resumed texts after opting out",
};

/** Every data column of the table, after the expand toggle. */
export const COLUMN_HEADERS = [
  "Number",
  "Status",
  "How we got it",
  "Added",
  "Opted in",
  "Opted out",
  "Line type",
  "Line type checked",
  "Delivery",
] as const;

/** The data columns plus the expand toggle, for full-width rows. */
export const SMS_CONSENT_COLUMN_COUNT = COLUMN_HEADERS.length + 1;

/** Shown in a date column that has no value. */
export const NO_DATE = "-";

/**
 * Dates arrive as ISO strings; the generated types call them `any`. Formatted
 * the same way as the organization messages page, so staff pages agree.
 */
export const formatDate = (value: unknown): string =>
  typeof value === "string" || value instanceof Date
    ? new Date(value).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : NO_DATE;

export const lineTypeLabel = (landline: boolean | null | undefined): string => {
  if (landline === true) {
    return "Landline";
  }
  if (landline === false) {
    return "Mobile";
  }
  return "Not checked";
};

/**
 * The delivery column's text: how many receipts in a row said the number
 * cannot take a text, and the last one's Twilio code and time.
 */
export const deliveryFailureLabel = ({
  consecutiveFailures,
  lastFailureCode,
  lastFailureAt,
}: {
  consecutiveFailures: number;
  lastFailureCode?: number | null;
  lastFailureAt?: unknown;
}): string => {
  if (consecutiveFailures === 0) {
    return "No recent failures";
  }
  const failures =
    consecutiveFailures === 1
      ? "1 failed text"
      : `${consecutiveFailures} failed texts in a row`;
  return `${failures}, last error ${lastFailureCode ?? "unknown"} on ${formatDate(lastFailureAt)}`;
};

/**
 * Whether texts to the number are being skipped right now. The stored window
 * outlives its end until a probe claims the next one or a delivery clears it,
 * so a past date means the next text goes out as the probe, not a skip.
 */
export const isSkippingTexts = (
  undeliverableUntil: unknown,
  now: Date = new Date(),
): boolean =>
  (typeof undeliverableUntil === "string" ||
    undeliverableUntil instanceof Date) &&
  new Date(undeliverableUntil).getTime() > now.getTime();

export type LineTypeFilter = "ALL" | "LANDLINE" | "MOBILE";

/** Turns the line type dropdown into the `landline` filter the API takes. */
export const landlineFilterValue = (filter: LineTypeFilter): boolean | null => {
  if (filter === "LANDLINE") {
    return true;
  }
  if (filter === "MOBILE") {
    return false;
  }
  return null;
};

/**
 * Why a full-number search found nothing. Rows are created only when a
 * consent-checked text is sent or the web form is used, so driver texts and
 * the opt-in confirmation never create one: "never texted" would be wrong.
 */
export const NO_RECORD_MESSAGE =
  "No consent record for this number: it has never received a consignee text and never used the opt-in form. Driver texts are not tracked here.";
